import { FormProvider } from 'react-hook-form';
import {
  OfferCodeInputLabels,
  OfferCodeInputVariables,
} from '@sparrow-card/sparrow-card-constants';
import ellipsesImage from 'assets/images/dark-ellipses.webp';
import { starlingDoubleShadowOptimized as shadowImage } from 'assets/images/optimized-images';
import creditCardImage from 'assets/images/starling-double-credit-card.webp';
import Button from 'components/Button';
import { ButtonVariant } from 'components/Button/Button';
import {
  OFFER_CODE_EMAIL_OR_MAIL_OFFER,
  OfferCodeInput,
  STANDARD_OFFER_CODE_MASK_OPTIONS,
  STANDARD_OFFER_CODE_PLACEHOLDER,
  ZIP_CODE_PLACEHOLDER,
  ZIP_MASK_OPTIONS,
  ZipInput,
} from 'components/Input/templates';
import Loading from 'components/Loading';
import ValidationError from 'components/ValidationError';
import OfferCodeLayout from 'layout/OfferCodeLayout';
import { OFFER_CODE_ZIP_RULES } from 'pages/OfferCode/offerCodeZipRules';
import useStarlingOfferForm from './useStarlingOfferForm';
import commonStyles from 'pages/OfferCode/OfferCode.module.scss';
import starlingStyles from 'pages/OfferCode/StarlingOfferCode/StarlingOfferCode.module.scss';

const LAYOUT_TITLE = 'Get Your Starling Card by Sparrow';
const LAYOUT_DESCRIPTION = 'To get started, please enter your Offer Code.';

const INPUTS_TO_MASKS_MAPPING = {
  [OfferCodeInputVariables.OfferCode]: STANDARD_OFFER_CODE_MASK_OPTIONS,
  [OfferCodeInputVariables.ZipCode]: ZIP_MASK_OPTIONS,
};

const StarlingOfferCode = () => {
  const {
    error,
    isInitialLoading,
    isLoading,
    isFormFilled,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept out of FormProvider methods
    offerData,
    ...methods
  } = useStarlingOfferForm(INPUTS_TO_MASKS_MAPPING);

  const { onSubmit, clearErrors } = methods;

  if (isInitialLoading) {
    return <Loading />;
  }

  return (
    <OfferCodeLayout
      description={LAYOUT_DESCRIPTION}
      title={LAYOUT_TITLE}
      previewImagePath={creditCardImage}
      ellipsesImagePath={ellipsesImage}
      shadowImagePath={shadowImage}
      iconContainerClassName={starlingStyles.starlingIconContainer}
    >
      <FormProvider {...methods}>
        <form
          onSubmit={onSubmit}
          id="starling-offer-code-form"
          noValidate
          className={commonStyles.form}
          aria-label="Starling offer code entry form"
        >
          <div className={commonStyles.inputsContainer}>
            <OfferCodeInput
              name={OfferCodeInputVariables.OfferCode}
              label={OfferCodeInputLabels.OfferCode}
              required
              placeholder={STANDARD_OFFER_CODE_PLACEHOLDER}
              maskOptions={STANDARD_OFFER_CODE_MASK_OPTIONS}
              containerClassName={commonStyles.input}
              tooltipText={OFFER_CODE_EMAIL_OR_MAIL_OFFER}
            />

            <ZipInput
              name={OfferCodeInputVariables.ZipCode}
              label={OfferCodeInputLabels.ZipCode}
              required
              placeholder={ZIP_CODE_PLACEHOLDER}
              rules={OFFER_CODE_ZIP_RULES}
              externalOnChange={() => {
                clearErrors(OfferCodeInputVariables.ZipCode);
              }}
              containerClassName={commonStyles.input}
            />
          </div>
          {!!error && (
            <ValidationError
              message={error}
              className={commonStyles.offerCodeError}
              role="alert"
              aria-live="assertive"
            />
          )}
          <Button
            type="submit"
            title="Get started"
            variant={ButtonVariant.Gradient}
            className={commonStyles.button}
            inactive={!isFormFilled}
            loading={isLoading}
          />
        </form>
      </FormProvider>
    </OfferCodeLayout>
  );
};

export default StarlingOfferCode;
